import React from 'react';
import { useFetch, fmtEur, fmtDateTime, statusStyles } from '../utils';
import { adminApi } from '../api';
import { Section } from '../_primitives/Section';
import { DataTable, type Column } from '../_primitives/DataTable';
import { RevenueDonut } from '../RevenueDonutLazy';
import type { RevenueData } from '../types';

type Payment = RevenueData['recent'][number];

const columns: Column<Payment>[] = [
  {
    key: 'created_at', label: 'Datum',
    sortValue: (p) => p.created_at,
    searchValue: (p) => p.created_at,
    render: (p) => <span className="text-ink/55 dark:text-white/55">{fmtDateTime(p.created_at)}</span>,
  },
  {
    key: 'email', label: 'Email',
    sortValue: (p) => p.email ?? '',
    searchValue: (p) => p.email ?? '',
    render: (p) => <span className="text-ink/85 dark:text-white/85 truncate">{p.email ?? '—'}</span>,
  },
  {
    key: 'type', label: 'Type',
    sortValue: (p) => p.type,
    searchValue: (p) => p.type,
    render: (p) => <span className="text-ink/65 dark:text-white/65">{p.type}</span>,
  },
  {
    key: 'description', label: 'Beschrijving',
    searchValue: (p) => p.description ?? '',
    render: (p) => <span className="text-ink/65 dark:text-white/65 truncate max-w-[28ch] inline-block" title={p.description ?? ''}>{p.description ?? '—'}</span>,
  },
  {
    key: 'amount_cents', label: 'Bedrag', align: 'right',
    sortValue: (p) => p.amount_cents,
    render: (p) => <span className="font-mono text-ink/85 dark:text-white/85">{fmtEur(p.amount_cents)}</span>,
  },
  {
    key: 'status', label: 'Status',
    sortValue: (p) => p.status,
    searchValue: (p) => p.status,
    render: (p) => <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-md ${statusStyles(p.status)}`}>{p.status}</span>,
  },
];

export const RevenueTab: React.FC = () => {
  const { data, loading, error, refresh } = useFetch(adminApi.revenue);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {[
          { label: 'Totaal omzet', value: data ? fmtEur(data.totals.total_revenue_cents) : '—' },
          { label: 'Openstaand', value: data ? fmtEur(data.totals.pending_cents) : '—' },
          { label: 'Aantal betalingen', value: data ? data.totals.total_payments.toLocaleString('nl-NL') : '—' },
        ].map((s) => (
          <div key={s.label} className="rev-tile">
            <p className="font-semibold uppercase tracking-[0.09em] mb-2" style={{ fontSize: '10.5px', color: 'var(--ink-55)' }}>{s.label}</p>
            <p className="num leading-none" style={{ fontSize: '30px', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--accent)' }}>{s.value}</p>
          </div>
        ))}
      </div>

      {data && <RevenueDonut planMix={data.planMix ?? []} planArpu={data.planArpu} />}

      <Section title="Recente betalingen" subtitle={data ? `Top ${data.recent.length}` : undefined}>
        <DataTable
          rows={data?.recent ?? []}
          loading={loading}
          error={error}
          onRetry={refresh}
          columns={columns}
          rowKey={(p) => p.id}
          searchPlaceholder="Zoek op email, type, beschrijving…"
        />
      </Section>
    </div>
  );
};
